import { baseApi } from "./baseApi";
import * as API from "@utils/apiPath";
import type {
  ApiResponse,
  SaveStepResponse,
  DriverTableFilterParams,
  DriverDetailsApiResponse,
} from "interface/driver";
import { StatusInterface } from "interface/common";

const getFormattedParams = (params: DriverTableFilterParams = {}) => {
  const normalizeFilterValue = (value?: string | string[] | null): string => {
    if (Array.isArray(value)) {
      return value[0] ?? "";
    }
    return value ?? "";
  };
  const normalizeStatusValue = (
    value?: Array<string | boolean> | string | boolean | null
  ): string => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item)).join("-");
    }
    return value !== null && value !== undefined ? String(value) : "";
  };
  const normalizeMultiFilterValue = (
    value?: string | string[] | null
  ): string => {
    if (Array.isArray(value)) {
      return value.length ? value.join("-") : "";
    }
    return value ?? "";
  };

  const formatted: Record<string, string | number> = {
    page: params.page ?? 1,
    limit: params.pageSize ?? 10,
  };

  const firstName = normalizeFilterValue(params.firstName);
  const email = normalizeFilterValue(params.email);
  const mobileNumber = normalizeFilterValue(params.mobile_number);
  const gender = normalizeMultiFilterValue(params.gender);
  const driverVehicleType = normalizeMultiFilterValue(params.driver_vehicle_type);
  const vehicleCategoryId = normalizeMultiFilterValue(
    params.vehicle_category_id
  );
  const status = normalizeStatusValue(
    params.status as string[] | null | undefined
  );
  const isOnline = normalizeStatusValue(
    params.is_online as string[] | null | undefined
  );
  const updatedAt = normalizeFilterValue(params.updatedAt);

  if (firstName) formatted.firstName = firstName;
  if (email) formatted.email = email;
  if (mobileNumber) formatted.mobile_number = mobileNumber;
  if (gender) formatted.gender = gender;
  if (driverVehicleType) formatted.driver_vehicle_type = driverVehicleType;
  if (vehicleCategoryId) formatted.vehicle_category_id = vehicleCategoryId;
  if (status) formatted.status = status;
  if (isOnline) formatted.is_online = isOnline;
  if (updatedAt) formatted.updatedAt = updatedAt;

  if (params.sortField) {
    formatted.sort = params.sortField;
    if (params.sortOrder) {
      formatted.direction = params.sortOrder === "ascend" ? "asc" : "desc";
    }
  }

  return formatted;
};

export interface DriverLiveLocation {
  id: string;
  firstName: string;
  lastName: string;
  latitude: number;
  longitude: number;
  vehicle_category: string;
  vehicle_category_image_url?: string;
  image_thumb_url?: string;
  /** Present for admin live map; defaults true when omitted (legacy socket payloads). */
  is_online?: boolean;
}

export type PendingEarningsOverviewResponse = {
  statusCode: number;
  message: string;
  data: {
    summary: {
      pendingBookingCount: number;
      totalTripRevenue: number;
      totalPendingDriverPayout: number;
      totalTovAmount: number;
      totalStripeFees: number;
    };
    drivers: {
      items: Array<{
        driverId: string;
        firstName: string;
        lastName: string;
        email: string;
        mobile_number: string;
        pendingBookingCount: number;
        totalTripRevenue: number;
        totalPendingDriverPayout: number;
        totalTovAmount: number;
        totalStripeFees: number;
      }>;
      pagination: { total: number; page: number; limit: number };
    };
  };
};

export const driverApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPendingEarningsOverview: builder.query<
      PendingEarningsOverviewResponse,
      { page?: number; limit?: number; driverId?: string }
    >({
      query: (params) => ({
        url: `${API.GET_DRIVER}/admin/pending-earnings-overview`,
        method: "GET",
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
          ...(params.driverId ? { driverId: params.driverId } : {}),
        },
      }),
      providesTags: ["PendingEarnings"],
    }),
    getCompletedEarningsOverview: builder.query<
      PendingEarningsOverviewResponse,
      { page?: number; limit?: number; driverId?: string }
    >({
      query: (params) => ({
        url: `${API.GET_DRIVER}/admin/completed-earnings-overview`,
        method: "GET",
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 10,
          ...(params.driverId ? { driverId: params.driverId } : {}),
        },
      }),
      providesTags: ["CompletedEarnings"],
    }),
    getDriverLiveLocations: builder.query<
      { statusCode: number; message: string; data: DriverLiveLocation[] },
      void
    >({
      query: () => ({
        url: `${API.GET_DRIVER}/live-locations`,
        method: "GET",
      }),
      providesTags: ["Driver"],
      /** Snapshot seed for the map; live updates use /booking-location socket. */
      keepUnusedDataFor: 86_400,
    }),
    getDrivers: builder.query<ApiResponse, DriverTableFilterParams>({
      query: (params) => ({
        url: API.GET_DRIVER,
        method: "GET",
        params: getFormattedParams(params),
      }),
      providesTags: ["Driver"],
    }),
    getDriverList: builder.query<ApiResponse, void>({
      query: () => ({
        url: `${API.GET_DRIVER}/list`,
        method: "GET",
      }),
      providesTags: ["Driver"],
    }),
    getDriverDetails: builder.query<
      DriverDetailsApiResponse,
      { id: string; currentStep?: number }
    >({
      query: ({ id, currentStep }) => ({
        url: `/${API.GET_DRIVER}/details/${id}`,
        method: "GET",
        params:
          currentStep && currentStep > 0
            ? {
                current_step: currentStep,
              }
            : undefined,
      }),
      providesTags: (_result, _error, { id }) => [{ type: "Driver", id }],
    }),
    getDriverViewDetails: builder.query<DriverDetailsApiResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/${API.GET_DRIVER}/${API.GET_DRIVER_DETAILS}/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { id }) => [{ type: "Driver", id }],
    }),
    getDriverUnsettledEarnings: builder.query<
      {
        statusCode: number;
        message: string;
        data: {
          items: Array<{
            _id: string;
            number: string;
            amount: string;
            tov_amount: string;
            driver_amount: string;
            stripeFees?: string | number;
            stripe_fees?: string | number;
            stripe_fee?: string | number;
            payment_method?: string;
            createdAt?: string;
          }>;
          summary: {
            totalAmount: number;
            totalTovAmount: number;
            totalDriverAmount: number;
            count: number;
          };
        };
      },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/${API.GET_DRIVER}/${id}/unsettled-earnings`,
        method: "GET",
      }),
      providesTags: (_result, _error, { id }) => [
        { type: "Driver", id },
        { type: "Driver", id: `${id}-earnings` },
      ],
    }),
    getDriverCompletedBookingsEarnings: builder.query<
      {
        statusCode: number;
        message: string;
        data: {
          items: Array<{
            _id: string;
            number: string;
            amount: string;
            tov_amount: string;
            driver_amount: string;
            stripeFees?: string | number;
            stripe_fees?: string | number;
            stripe_fee?: string | number;
            payment_method?: string;
            is_settled: boolean;
            createdAt?: string;
          }>;
          summary: {
            totalAmount: number;
            totalTovAmount: number;
            totalDriverAmount: number;
            count: number;
          };
        };
      },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/${API.GET_DRIVER}/${id}/completed-bookings-earnings`,
        method: "GET",
      }),
      providesTags: (_result, _error, { id }) => [
        { type: "Driver", id },
        { type: "Driver", id: `${id}-completed-earnings` },
      ],
    }),
    settleDriverEarnings: builder.mutation<
      { statusCode: number; message: string; data: { modifiedCount: number } },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/${API.GET_DRIVER}/${id}/settle-earnings`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Driver", id },
        { type: "Driver", id: `${id}-earnings` },
        { type: "Driver", id: `${id}-completed-earnings` },
        "PendingEarnings",
        "CompletedEarnings",
      ],
    }),
    downloadEarningsOverview: builder.mutation<
      { statusCode: number; message: string; data: { fileUrl?: string } },
      { variant: "awaiting_payout" | "all_completed"; driverId?: string }
    >({
      query: ({ variant, driverId }) => ({
        url:
          variant === "awaiting_payout"
            ? `/${API.GET_DRIVER}/admin/pending-earnings-download`
            : `/${API.GET_DRIVER}/admin/completed-earnings-download`,
        method: "GET",
        params: driverId ? { driverId } : {},
      }),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          const fileUrl = result.data?.data?.fileUrl;
          if (fileUrl) {
            window.open(fileUrl, "_blank");
          }
        } catch (error) {
          console.error("Failed to download earnings overview:", error);
        }
      },
    }),
    updateDriverStatus: builder.mutation<ApiResponse, StatusInterface>({
      query: ({ id, ...rest }) => ({
        url: `/${API.GET_DRIVER}/status/${id}`,
        method: "PUT",
        body: rest,
      }),
      invalidatesTags: ["Driver"],
    }),
    downloadDriverReport: builder.mutation<ApiResponse, DriverTableFilterParams | void>({
      query: (params) => ({
        url: `${API.GET_DRIVER}/download`,
        method: "GET",
        params: getFormattedParams(params ?? {}),
      }),
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          const fileName = result.data.data.fileUrl;
          if (fileName) {
            window.open(fileName, "_blank");
          }
        } catch (error) {
          console.error("Failed to download driver report:", error);
        }
      },
    }),
    updateDriverKycStatus: builder.mutation<
      ApiResponse,
      { id: string; kyc_status: "approved" | "reject"; reason?: string }
    >({
      query: ({ id, ...rest }) => ({
        url: `/${API.GET_DRIVER}/kyc-status/${id}`,
        method: "PUT",
        body: rest,
      }),
      invalidatesTags: ["Driver"],
    }),
    deleteDriver: builder.mutation<ApiResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/${API.GET_DRIVER}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Driver"],
    }),

    saveDriverBasicDetails: builder.mutation<SaveStepResponse, FormData>({
      query: (formData) => ({
        url: `${API.GET_DRIVER}/basic-details`,
        method: "POST",
        body: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      invalidatesTags: ["Driver"],
    }),
    saveDriverComplianceDocuments: builder.mutation<SaveStepResponse, FormData>({
      query: (formData) => ({
        url: `${API.GET_DRIVER}/compliance-documents`,
        method: "POST",
        body: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      invalidatesTags: ["Driver"],
    }),
    saveDriverVehicleDetails: builder.mutation<SaveStepResponse, FormData>({
      query: (formData) => ({
        url: `${API.GET_DRIVER}/vehicle-details`,
        method: "POST",
        body: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      invalidatesTags: ["Driver"],
    }),
    saveDriverBankAccount: builder.mutation<
      SaveStepResponse,
      {
        driverId: string;
  
        routing_number: string;
        account_number: string;
      }
    >({
      query: (body) => ({
        url: `${API.GET_DRIVER}/admin-bank-account-onboarding`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Driver"],
    }),
    saveDriverDigitalSignature: builder.mutation<SaveStepResponse, FormData>({
      query: (formData) => ({
        url: `${API.GET_DRIVER}/digital-signature`,
        method: "POST",
        body: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      invalidatesTags: ["Driver"],
    }),
  }),
});

export const {
  useGetPendingEarningsOverviewQuery,
  useGetCompletedEarningsOverviewQuery,
  useGetDriversQuery,
  useGetDriverListQuery,
  useGetDriverLiveLocationsQuery,
  useGetDriverDetailsQuery,
  useGetDriverViewDetailsQuery,
  useGetDriverUnsettledEarningsQuery,
  useGetDriverCompletedBookingsEarningsQuery,
  useSettleDriverEarningsMutation,
  useDownloadEarningsOverviewMutation,
  useUpdateDriverStatusMutation,
  useDownloadDriverReportMutation,
  useUpdateDriverKycStatusMutation,
  useDeleteDriverMutation,
  useSaveDriverBasicDetailsMutation,
  useSaveDriverComplianceDocumentsMutation,
  useSaveDriverVehicleDetailsMutation,
  useSaveDriverBankAccountMutation,
  useSaveDriverDigitalSignatureMutation,
} = driverApi;
